import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { setStoredLanguage } from "@/i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Whenever the authed user's `language` changes (login, refresh, profile
  // save), apply it platform-wide via i18n. This guarantees a single source
  // of truth: the user's account setting drives every translated string.
  useEffect(() => {
    if (user && typeof user === "object" && user.language) {
      setStoredLanguage(user.language);
    }
  }, [user]);

  // Stable per-browser device id. Never rotated — even after logout — so we
  // can recognize this browser on next login without forcing 2FA every time.
  const getDeviceId = () => {
    let id = localStorage.getItem("device_id");
    if (!id) {
      const arr = new Uint8Array(16);
      (window.crypto || window.msCrypto).getRandomValues(arr);
      id = Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
      localStorage.setItem("device_id", id);
    }
    return id;
  };

  const login = async (username, password) => {
    try {
      const device_id = getDeviceId();
      const { data } = await api.post("/auth/login-2fa/start", { username, password, device_id });
      if (data.requires_2fa) {
        // Caller must complete verification. Return challenge context.
        return {
          ok: false,
          requires_2fa: true,
          challenge_id: data.challenge_id,
          email_masked: data.email_masked,
          admin_verification: !!data.admin_verification,
          admin_email_masked: data.admin_email_masked,
          message: data.message,
          device_id: data.device_id || device_id,
          delivery_configured: data.delivery_configured,
          resend_cooldown_seconds: data.resend_cooldown_seconds,
          code_ttl_minutes: data.code_ttl_minutes,
        };
      }
      // Trusted device — token issued straight away
      localStorage.setItem("token", data.access_token);
      setUser(data.user);
      return { ok: true, trusted: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const verify2fa = async ({ challenge_id, code, trust_device, device_name }) => {
    try {
      const device_id = getDeviceId();
      const { data } = await api.post("/auth/login-2fa/verify", {
        challenge_id, code, trust_device, device_id, device_name,
      });
      localStorage.setItem("token", data.access_token);
      setUser(data.user);
      return { ok: true, trusted: data.trusted, trust_error: data.trust_error };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const resend2fa = async (challenge_id) => {
    try {
      const { data } = await api.post("/auth/login-2fa/resend", { challenge_id });
      return { ok: true, delivery_configured: data.delivery_configured };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verify2fa, resend2fa, logout, refresh, refreshUser: refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
